import { useMutation } from "react-query";
import { postAuthOTPSend } from "../../../queries/auth";
import { coreService } from "../../../services/coreService";
import { FirstArgumentType } from "../../../types/ArgumentTypes";
import { useAnonymousTokenNetworkHeaders } from "./useAnonymousTokenNetworkHeaders";
import { useEffect, useState } from "react";

export function useAuthOTPSend({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (data: Awaited<ReturnType<ReturnType<typeof postAuthOTPSend>>>) => void;
  onError?: (error: unknown, message: string) => void;
} = {}) {
  const networkHeaders = useAnonymousTokenNetworkHeaders();
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  useEffect(() => {
    if (!cooldownEndsAt) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setRemainingSeconds(remaining);

      if (remaining === 0) {
        setCooldownEndsAt(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  const [authOTPSendMutation, { isLoading: isAuthOTPSendLoading }] = useMutation(postAuthOTPSend(networkHeaders), {
    onSuccess: (data) => {
      setCooldownEndsAt(Date.now() + 60 * 1000);
      setRemainingSeconds(60);
      onSuccess(data);
    },
    onError: (error) => {
      if ((error as any).status === 429) {
        const message = (error as any).error.message;
        onError(error, message);
      } else {
        onError(error, "An unknown error occurred while sending one-time password");
      }
    },
  });

  const sendAuthOTP = async (body: FirstArgumentType<typeof coreService.authOtpSendCreate>) => {
    return await authOTPSendMutation(body);
  };

  return { sendAuthOTP, isAuthOTPSendLoading, remainingSeconds };
}
